import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';

// Theme
import { ThemeProvider } from './contexts/ThemeContext';

// Auth
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AssistantProvider } from './contexts/AssistantContext';
import ProtectedRoute from './components/ProtectedRoute';

// Layout
import { AppLayout } from './components/AppLayout';
import AdminLayout from './layouts/AdminLayout';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmailVerification from './pages/EmailVerification';
import AcceptInvitation from './pages/AcceptInvitation';
import VerifyEmailInterstitial from './pages/VerifyEmailInterstitial';

// Protected Pages
import Dashboard from './pages/Dashboard';
import HeuresPage from './pages/HeuresPage';
import Contracts from './pages/Contracts';
import JoinByCode from './pages/JoinByCode';
import Payroll from './pages/Payroll';
import Billing from './pages/Billing';
import Legal from './pages/Legal';
import Profile from './pages/Profile';
import ChildHealth from './pages/ChildHealth';
import ContributionSettings from './pages/ContributionSettings';
import LeaveManagement from './pages/LeaveManagement';
import DevTools from './pages/DevTools';
import Support from './pages/Support';
import TicketDetailUser from './pages/TicketDetailUser';
import FAQ from './pages/FAQ';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminUserDetail from './pages/admin/UserDetail';
import AdminAudit from './pages/admin/Audit';
import AISettings from './pages/admin/settings/AISettings';
import AICosts from './pages/admin/AICosts';
import SupportDashboard from './pages/admin/support/Dashboard';
import SupportTickets from './pages/admin/support/Tickets';
import SupportTicketDetail from './pages/admin/support/TicketDetail';
import SupportActions from './pages/admin/support/Actions';

// Public Pages
import Landing from './pages/Landing';
import Onboarding from './pages/Onboarding';

// Legal page: show AppLayout+nav when logged in, plain page when not
const LegalRoute = () => {
  const { user } = useAuth();
  if (user) {
    return <AppLayout><Legal /></AppLayout>;
  }
  return <Legal />;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <AssistantWrapper>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email/:token" element={<EmailVerification />} />
          <Route path="/accept-invite/:token" element={<AcceptInvitation />} />

          {/* Email verification interstitial — protected but skips email check */}
          <Route path="/verifier-email" element={
            <ProtectedRoute skipEmailCheck>
              <VerifyEmailInterstitial />
            </ProtectedRoute>
          } />
          
          {/* Protected routes — AppLayout includes Navigation */}
          <Route path="/accueil" element={
            <ProtectedRoute>
              <AppLayout><Dashboard /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/heures" element={
            <ProtectedRoute>
              <AppLayout><HeuresPage /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/contrats" element={
            <ProtectedRoute>
              <AppLayout><Contracts /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/join" element={
            <ProtectedRoute>
              <AppLayout><JoinByCode /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/paie" element={
            <ProtectedRoute>
              <AppLayout><Payroll /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/dev" element={
            <ProtectedRoute>
              <AppLayout><DevTools /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/billing" element={
            <ProtectedRoute>
              <AppLayout><Billing /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/legal" element={<LegalRoute />} />
          
          <Route path="/profil" element={
            <ProtectedRoute>
              <AppLayout><Profile /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/contrats/:contractId/sante" element={
            <ProtectedRoute>
              <AppLayout><ChildHealth /></AppLayout>
            </ProtectedRoute>
          } />
          
          {/* SIMULATEUR - DÉSACTIVÉ */}
          {/* <Route path="/simulateur" element={
            <ProtectedRoute>
              <AppLayout><ContributionSettings /></AppLayout>
            </ProtectedRoute>
          } /> */}
          
          <Route path="/conges" element={
            <ProtectedRoute>
              <AppLayout><LeaveManagement /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/support" element={
            <ProtectedRoute>
              <AppLayout><Support /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/support/tickets/:ticketId" element={
            <ProtectedRoute>
              <AppLayout><TicketDetailUser /></AppLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/faq" element={
            <ProtectedRoute>
              <AppLayout><FAQ /></AppLayout>
            </ProtectedRoute>
          } />
          
          {/* Admin routes — requires admin or support role */}
          <Route path="/admin" element={
            <ProtectedRoute requireRole={['admin', 'support']}>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:userId" element={<AdminUserDetail />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="settings/ai" element={<AISettings />} />
            <Route path="ai-costs" element={<AICosts />} />
            <Route path="support" element={<SupportDashboard />} />
            <Route path="support/tickets" element={<SupportTickets />} />
            <Route path="support/tickets/:ticketId" element={<SupportTicketDetail />} />
            <Route path="support/actions" element={<SupportActions />} />
          </Route>
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" />
        </AssistantWrapper>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

// Wrapper conditionnel pour l'assistant
function AssistantWrapper({ children }) {
  const { user } = useAuth();
  
  // Ne charger l'assistant que si utilisateur connecté
  if (!user) {
    return children;
  }

  // Import dynamique des composants
  const AssistantButton = React.lazy(() => import('./components/assistant/AssistantButton'));
  const AssistantChat = React.lazy(() => import('./components/assistant/AssistantChat'));

  return (
    <AssistantProvider>
      {children}
      <React.Suspense fallback={null}>
        <AssistantButton />
        <AssistantChat />
      </React.Suspense>
    </AssistantProvider>
  );
}

export default App;
